import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { saveVisite } from "../services/visitesServices";

const IGNORED_PREFIXES = [
  "/appli",
  "/admin",
  "/dashboard",
  "/login",
  "/connexion",
  "/register",
  "/inscription",
  "/mot-de-passe",
  "/reset-password",
  "/forgot-password",
  "/profil",
  "/profile",
  "/mon-compte",
  "/api",
];

const PAGE_LABELS = {
  home: "Accueil",
  "/actualites": "Actualités",
  "/agenda": "Agenda",
  "/associations": "Associations",
  "/commerces": "Commerces",
  "/services-municipaux": "Services municipaux",
  "/trombinoscope": "Élus municipaux",
  "/carte-interets": "Carte interactive",
  "/grands-projets": "Grands projets",
  "/publications": "Publications",
  "/documents": "Documents",
  "/recherche": "Recherche",
  "/contact": "Contact",
  "/mentions-legales": "Mentions légales",
  "/politique-confidentialite": "Politique de confidentialité",
  "/conditions-utilisation": "Conditions d’utilisation",
  "/faq": "FAQ",
};

const TRACKING_GUARD_DELAY = 15000;

function normalizePath(pathname) {
  let path = String(pathname || "/").trim();

  if (!path || path === "/") {
    return "home";
  }

  if (!path.startsWith("/")) {
    path = `/${path}`;
  }

  path = path.split("?")[0].split("#")[0].trim();
  path = path.replace(/\/{2,}/g, "/");

  if (path.length > 1) {
    path = path.replace(/\/$/, "");
  }

  return path;
}

function isIgnoredPath(pagePath) {
  return IGNORED_PREFIXES.some(
    (prefix) => pagePath === prefix || pagePath.startsWith(`${prefix}/`)
  );
}

function getPageLabel(pagePath) {
  if (PAGE_LABELS[pagePath]) {
    return PAGE_LABELS[pagePath];
  }

  if (pagePath.startsWith("/page/")) {
    return `Page personnalisée : ${pagePath.replace("/page/", "")}`;
  }

  if (pagePath.startsWith("/commerces/")) {
    return "Fiche commerce";
  }

  const title = document.title?.trim();

  if (title) {
    return title.slice(0, 180);
  }

  return pagePath;
}

function getGuardKey(pagePath) {
  return `mam_tracking_guard_${pagePath}`;
}

function shouldSkipRecentTracking(pagePath) {
  try {
    const key = getGuardKey(pagePath);
    const lastTrackedAt = Number(sessionStorage.getItem(key) || 0);
    const now = Date.now();

    return now - lastTrackedAt < TRACKING_GUARD_DELAY;
  } catch (_) {
    return false;
  }
}

function markTracked(pagePath) {
  try {
    const key = getGuardKey(pagePath);
    sessionStorage.setItem(key, String(Date.now()));
  } catch (_) {
    // Rien à faire si sessionStorage est indisponible
  }
}

export default function usePageVisitTracker() {
  const location = useLocation();

  useEffect(() => {
    const pagePath = normalizePath(location.pathname);

    if (isIgnoredPath(pagePath)) {
      return;
    }

    if (shouldSkipRecentTracking(pagePath)) {
      return;
    }

    const pageLabel = getPageLabel(pagePath);

    markTracked(pagePath);

    saveVisite({
      page_path: pagePath,
      page_label: pageLabel,
    }).catch((error) => {
      console.error("Erreur lors du tracking de la page :", error);
    });
  }, [location.pathname]);
}